"use client"

// Read-only hook for the family picker payload. Returns the LIGHT shape —
// `ProductFamilyLight[]` (id, name, family_key, product_count) — which is
// what the picker UI actually needs to render. Per-product hydration is
// available on demand via `hydrateFamily(id)` which calls
// GET /api/integrations/shopify/product-families/[id] for the single rule
// the user selected. Rules themselves are managed on the Product Families
// settings page; that's why there are no save/delete actions here.
//
// `product_count` has two modes:
//   - **Period-scoped** (`period !== null`) — the count matches what the
//     consumer's economics row will compute after selection (same period
//     + minimum_order_total filter as getOrderIdsForPeriod). Used by the
//     Economics page Add Family picker. Period changes trigger a refetch.
//   - **All-time** (`period === null`) — count comes from line_items only,
//     matching `get_products_catalog`'s catalog-membership count. Used
//     by the COGS page «Filter by family» dropdown which is
//     period-independent.
//
// **Module-level browser cache** — once the light list is loaded for a
// (auth scope, integration, period, minimumOrderTotal) tuple, re-opening
// the picker on the same combination resolves synchronously without a
// round-trip. Same for `hydrateFamily(id)` — keyed by (auth scope,
// integration, familyId) because the per-id hydrate is catalog-wide, not
// period-scoped.
//
// **Auth-scoped keys.** Cache keys include `user_id` AND the effective
// tenant id (`impersonated_tenant_id ?? tenant_id`) so impersonation
// switches and login/logout cycles never serve another user's data
// synchronously without re-hitting route auth. Caches are also explicitly
// cleared when `isAuthenticated` flips to false (logout).
//
// **Mutation invalidation.** Family-rule mutations (create / update /
// remove / reorder in `use-bundle-rules.ts`, plus wizard finalization in
// `bundle-rules-page.tsx onComplete`) call
// `invalidateProductFamiliesCache(integrationId)` so a tenant editing a
// rule on the Product Families page won't see stale picker data when they
// return to Economics.
//
// Cache lives for the SPA session (lost on hard reload).
//
// Stale-response seq guard mirrors useBundlePerformance — increment on every
// fetch so a slow in-flight response can't overwrite UI state set by a later
// fetch (e.g. integrationId / period switch mid-flight).

import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type { ProductFamily, ProductFamilyLight } from "@/types/shopify"

// ---------------------------------------------------------------------------
// Module-level caches (SPA-session, lost on hard reload)
// ---------------------------------------------------------------------------

// Key shape: `${userId}|${effectiveTenantId}|${integrationId}|${monthStartUtc}|${monthEndUtc}|${minimumOrderTotal}`
// for the light list, and `${userId}|${effectiveTenantId}|${integrationId}|${familyId}`
// for the hydrate. integrationId is at index 2 in both — see scanByIntegration.
const KEY_SEP = "|"
const INTEGRATION_KEY_SEGMENT = 2

const lightCache = new Map<string, ProductFamilyLight[]>()
const hydrateCache = new Map<string, ProductFamily>()

interface AuthScope {
  userId: string
  effectiveTenantId: string
}

function lightCacheKey(
  scope: AuthScope,
  integrationId: string,
  period: UseProductFamiliesPeriod | null,
): string {
  // Sentinel «alltime» distinguishes the no-period mode from a hypothetical
  // empty-string period (which the validation in load() rejects anyway,
  // but cache keys should be unambiguous).
  const periodSegment = period
    ? `${period.monthStartUtc}|${period.monthEndUtc}|${period.minimumOrderTotal}`
    : "alltime"
  return [scope.userId, scope.effectiveTenantId, integrationId, periodSegment].join(KEY_SEP)
}

function hydrateCacheKey(
  scope: AuthScope,
  integrationId: string,
  familyId: string,
): string {
  return [scope.userId, scope.effectiveTenantId, integrationId, familyId].join(KEY_SEP)
}

function scanByIntegration(map: Map<string, unknown>, integrationId: string): string[] {
  const matches: string[] = []
  for (const k of map.keys()) {
    const segs = k.split(KEY_SEP)
    if (segs[INTEGRATION_KEY_SEGMENT] === integrationId) matches.push(k)
  }
  return matches
}

/**
 * Invalidate cached entries for one integration across all auth scopes.
 * Called from family-rule mutation paths (`useFamilyRules` create / update
 * / remove / reorder) and from the wizard's onComplete in
 * `bundle-rules-page.tsx`. Pass no argument to clear everything (e.g. on
 * tenant switch / logout).
 */
export function invalidateProductFamiliesCache(integrationId?: string): void {
  if (!integrationId) {
    lightCache.clear()
    hydrateCache.clear()
    return
  }
  for (const k of scanByIntegration(lightCache as Map<string, unknown>, integrationId)) {
    lightCache.delete(k)
  }
  for (const k of scanByIntegration(hydrateCache as Map<string, unknown>, integrationId)) {
    hydrateCache.delete(k)
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseProductFamiliesPeriod {
  /** ISO 8601 timestamp; lower bound (inclusive). */
  monthStartUtc: string
  /** ISO 8601 timestamp; upper bound (exclusive). */
  monthEndUtc: string
  /** Same `minimum_order_total` semantic as the rest of the Economics view. */
  minimumOrderTotal: number
}

interface UseProductFamiliesOptions {
  integrationId: string
  /**
   * Period scope for the picker count. Pass an object for period-scoped
   * count (Economics page); pass `null` for all-time count (COGS page,
   * matches `get_products_catalog`'s catalog-membership semantic).
   */
  period: UseProductFamiliesPeriod | null
  enabled?: boolean
}

interface UseProductFamiliesReturn {
  families: ProductFamilyLight[]
  isLoading: boolean
  error: string | null
  /** Force-refetch (busts the integration's cache for this hook + hydrate). */
  refresh: () => Promise<void>
  /**
   * Fetch the full ProductFamily for one rule (id, name, family_key,
   * products: ProductOption[]). Cached per (auth scope, integration,
   * familyId) for the SPA session. Returns null when the id isn't a valid
   * enabled rule for this integration (404).
   */
  hydrateFamily: (familyId: string) => Promise<ProductFamily | null>
}

export function useProductFamilies({
  integrationId,
  period,
  enabled = true,
}: UseProductFamiliesOptions): UseProductFamiliesReturn {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const fetchRef = useRef(fetchWithAuth)
  fetchRef.current = fetchWithAuth

  const [families, setFamilies] = useState<ProductFamilyLight[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestSeqRef = useRef(0)

  // Primitive period deps — same rationale as the auth-scope split below.
  // `period` could be a fresh object literal each render from a parent that
  // doesn't memoize, which would retrigger loadInternal's deps and cause an
  // infinite loop. Reading primitives here keeps deps stable when nothing
  // changed semantically.
  const monthStartUtc = period?.monthStartUtc ?? null
  const monthEndUtc = period?.monthEndUtc ?? null
  const minimumOrderTotal = period?.minimumOrderTotal ?? 0

  // Auth scope — primitive deps so referential identity is stable across
  // renders that don't actually change the user / tenant. Building a
  // `{ userId, effectiveTenantId }` object inline would create a new
  // identity every render, retriggering the load useEffect → setState →
  // re-render loop (Maximum update depth exceeded).
  const userId = user?.user_id ?? null
  const effectiveTenantId = user?.impersonated_tenant_id ?? user?.tenant_id ?? null

  // Clear caches on logout transitions. Login of a new user produces new
  // auth-scoped keys naturally; old entries are just orphaned and pressure
  // GC, but tearing down on logout keeps the picture tidy and avoids
  // unbounded growth across long sessions of switching users.
  useEffect(() => {
    if (!isAuthenticated) {
      invalidateProductFamiliesCache()
    }
  }, [isAuthenticated])

  const loadInternal = useCallback(
    async (forceRefresh: boolean) => {
      const localSeq = ++requestSeqRef.current
      // Period: either both bounds present (period-scoped mode) or both
      // missing (all-time mode). Mixed state from a misuse — bail.
      const hasPeriod = !!monthStartUtc && !!monthEndUtc
      const periodMisuse = (!!monthStartUtc) !== (!!monthEndUtc)
      if (
        !enabled ||
        !integrationId ||
        !isAuthenticated ||
        !userId ||
        !effectiveTenantId ||
        periodMisuse
      ) {
        if (localSeq === requestSeqRef.current) {
          setFamilies([])
          setError(null)
          setIsLoading(false)
        }
        return
      }

      const scope: AuthScope = { userId, effectiveTenantId }
      const periodForKey: UseProductFamiliesPeriod | null = hasPeriod
        ? { monthStartUtc, monthEndUtc, minimumOrderTotal }
        : null
      const cacheKey = lightCacheKey(scope, integrationId, periodForKey)
      if (!forceRefresh) {
        const cached = lightCache.get(cacheKey)
        if (cached && localSeq === requestSeqRef.current) {
          setFamilies(cached)
          setError(null)
          setIsLoading(false)
          return
        }
      } else {
        lightCache.delete(cacheKey)
      }

      setIsLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({ integration_id: integrationId })
        if (hasPeriod) {
          // Non-null assertions are safe here: hasPeriod => both bounds set.
          params.set("month_start_utc", monthStartUtc!)
          params.set("month_end_utc", monthEndUtc!)
          params.set("minimum_order_total", String(minimumOrderTotal))
        }
        const res = await fetchRef.current(
          `/api/integrations/shopify/product-families?${params}`,
        )
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        const data = (await res.json()) as { families: ProductFamilyLight[] }
        const next = data.families ?? []
        lightCache.set(cacheKey, next)
        if (localSeq === requestSeqRef.current) {
          setFamilies(next)
        }
      } catch (err: unknown) {
        if (localSeq === requestSeqRef.current) {
          const message = err instanceof Error ? err.message : "Failed to load product families"
          setError(message)
          setFamilies([])
        }
      } finally {
        if (localSeq === requestSeqRef.current) setIsLoading(false)
      }
    },
    [
      enabled,
      integrationId,
      isAuthenticated,
      userId,
      effectiveTenantId,
      monthStartUtc,
      monthEndUtc,
      minimumOrderTotal,
    ],
  )

  const load = useCallback(() => loadInternal(false), [loadInternal])
  const refresh = useCallback(async () => {
    invalidateProductFamiliesCache(integrationId)
    await loadInternal(true)
  }, [integrationId, loadInternal])

  useEffect(() => {
    void load()
  }, [load])

  const hydrateFamily = useCallback(
    async (familyId: string): Promise<ProductFamily | null> => {
      if (!integrationId || !isAuthenticated || !userId || !effectiveTenantId) return null
      const scope: AuthScope = { userId, effectiveTenantId }
      const key = hydrateCacheKey(scope, integrationId, familyId)
      const cached = hydrateCache.get(key)
      if (cached) return cached
      const params = new URLSearchParams({ integration_id: integrationId })
      const res = await fetchRef.current(
        `/api/integrations/shopify/product-families/${encodeURIComponent(familyId)}?${params}`,
      )
      if (res.status === 404) return null
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      const data = (await res.json()) as { family: ProductFamily }
      const family = data.family ?? null
      if (family) hydrateCache.set(key, family)
      return family
    },
    [integrationId, isAuthenticated, userId, effectiveTenantId],
  )

  return { families, isLoading, error, refresh, hydrateFamily }
}
